import React, { useState } from 'react';
import { CompleteAssessment, DISCColor } from './types';
import ProfileDashboard from './ProfileDashboard';
import { ProfileIcon, AnalyticsIcon, QuickIcon, ErrorIcon } from '../../../icons';
import { Star } from 'lucide-react';

interface ProfileWidgetProps {
  assessment: CompleteAssessment;
}

/**
 * Profile Widget Component
 * 
 * Compact version for JourneyPage - expandable to full dashboard
 */
const ProfileWidget: React.FC<ProfileWidgetProps> = ({ assessment }) => {
  const [showFullProfile, setShowFullProfile] = useState(false);
  const { discProfile, workstyleProfile, assessmentLevel } = assessment;

  const getColorDot = (color: DISCColor) => {
    const colorClasses = {
      red: 'bg-red-500',
      yellow: 'bg-yellow-400',
      green: 'bg-green-500',
      blue: 'bg-blue-500'
    };
    return <span className={`inline-block w-3 h-3 rounded-full ${colorClasses[color]}`} />;
  };

  const getColorName = (color: DISCColor) => {
    const names = {
      red: 'Rood',
      yellow: 'Geel',
      green: 'Groen',
      blue: 'Blauw'
    };
    return names[color];
  };

  if (showFullProfile) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full my-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2"><ProfileIcon size={22} className="text-white" /> Jouw Compleet Profiel</h2>
            <button
              onClick={() => setShowFullProfile(false)}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ✕
            </button>
          </div>
          <div className="p-6">
            <ProfileDashboard assessment={assessment} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <button
        onClick={() => setShowFullProfile(true)}
        className="w-full text-left hover:bg-gray-50 transition-colors rounded-lg p-2 -m-2"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ProfileIcon size={18} className="text-gray-700" />
            <span>Jouw Profiel</span>
          </h3>
          <span className="text-xs text-blue-600 font-medium">Klik voor details →</span>
        </div>

        {/* Quick Stats */}
        <div className="space-y-2">
          {/* DISC */}
          {discProfile && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600 flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-gradient-to-r from-purple-400 to-pink-400" /> DISC:</span>
              <span className="font-medium text-gray-900 flex items-center gap-1">
                {getColorDot(discProfile.primary)} {getColorName(discProfile.primary)} / 
                {getColorDot(discProfile.secondary)} {getColorName(discProfile.secondary)}
              </span>
            </div>
          )}

          {/* Werkstijl */}
          {workstyleProfile && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600 flex items-center gap-1"><QuickIcon size={14} className="text-gray-500" /> Werkstijl:</span>
              <span className="font-medium text-gray-900">
                Quick Start {workstyleProfile.scores.quick_start}/10
              </span>
            </div>
          )}

          {/* Assessment Level */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 flex items-center gap-1"><AnalyticsIcon size={14} className="text-gray-500" /> Niveau:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              assessmentLevel === 'full' 
                ? 'bg-green-100 text-green-700'
                : assessmentLevel === 'basic'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-200 text-gray-600'
            }`}>
              {assessmentLevel === 'full' && <span className="flex items-center gap-1"><Star size={12} className="text-green-600" /> Volledig</span>}
              {assessmentLevel === 'basic' && <span className="flex items-center gap-1"><QuickIcon size={12} className="text-blue-600" /> Basis</span>}
              {assessmentLevel === 'none' && <span className="flex items-center gap-1"><ErrorIcon size={12} className="text-gray-500" /> Geen</span>}
            </span>
          </div>
        </div>

        {/* Quick Insights */}
        {discProfile && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-600">
              <strong>Sterkte:</strong> {discProfile.entrepreneurialStrengths[0]}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              <strong>Let op:</strong> {discProfile.entrepreneurialWeaknesses[0]}
            </p>
          </div>
        )}
      </button>
    </div>
  );
};

export default ProfileWidget;
